import React, { useEffect, useState } from "react"
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Breadcrumbs,
  BreadcrumbItem,
  Chip,
} from "@nextui-org/react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faArrowAltCircleRight, faChevronLeft } from "@fortawesome/free-solid-svg-icons"
import { ToastContainer } from "react-toastify"
import "react-toastify/dist/ReactToastify.css" //react toastify css
import { jwtDecode } from "jwt-decode"
import { useDispatch, useSelector } from "react-redux"
import { setTokenExpiry } from "../state/authSlice"
import { isMobile } from "react-device-detect"

export default function MainContainer({
  children,
  info = [],
  pageName = "",
  breadcrumbs = [],
  prevPage = {},
}) {
  const [expiryTime, setExpiryTime] = useState(null);

  const { token } = useSelector((state) => state.auth)
  const dispatch = useDispatch();

  useEffect(() => {

    if (token && token != "") {

      const decoded = jwtDecode(token);

      // Extract the expiration time and convert it to a human-readable format
      const expirationTime = new Date(decoded.exp * 1000); // Convert from seconds to milliseconds

      let hours = expirationTime.getHours();
      const minutes = expirationTime.getMinutes();
      const seconds = expirationTime.getSeconds();
      const ampm = hours >= 12 ? 'PM' : 'AM';

      // Convert 24-hour format to 12-hour format
      hours = hours % 12;
      hours = hours ? hours : 12; // 0 becomes 12 (for midnight)

      // Format minutes and seconds to always show two digits
      const formattedTime = `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')} `;

      setExpiryTime(formattedTime + " " + ampm); // Show in a readable format

      dispatch(setTokenExpiry(expirationTime.getTime() / 1000))
    }
  }, []);

  return (
    <Card radius="none" shadow="sm" className={`${isMobile?"top-10":""} "rounded-none min-h-screen lg:px-20 z-1"`}>
      {/* lg:ml-64 */}
      <ToastContainer />
      <CardHeader className="flex p-1 w-full flex-col gap-1 
      bg-gradient-to-br from-white to-lfime-50 border-0 border-wi-200
       items-start sticky align-top">
       <div className="w-full">

  {/* ================= TOP BAR ================= */}
  <div
    className="
      flex w-full
      flex-wrap items-center
      justify-between
      gap-2
      rounded-t-lg
      border-b border-slate-200
      bg-white
      px-3 py-1
      shadow-sm
      dark:border-slate-700
      dark:bg-slate-900
    "
  >

    {/* Breadcrumbs */}
    <div className="min-w-0 flex-1">

      {breadcrumbs?.length > 0 && (
        <Breadcrumbs
          variant="light"
          size="sm"
          color="warning"
          className="text-xs"
          classNames={{
            list: "gap-1",
          }}
        >
          {breadcrumbs?.map((item, index) => (
            <BreadcrumbItem
              key={`${item}-${index}`}
              className="
                text-xs
                font-semibold
                text-slate-500
                transition-colors
                hover:text-indigo-600
                dark:text-slate-400
                dark:hover:text-indigo-400
              "
            >
              {item}
            </BreadcrumbItem>
          ))}
        </Breadcrumbs>
      )}

    </div>


    {/* Session Expiry */}
    <div
      className="
        flex items-center gap-1.5
        rounded-md
        border border-rose-200
        bg-rose-50
        px-2.5 py-0.5
        text-[11px]
        font-semibold
        text-rose-600
        whitespace-nowrap
        dark:border-rose-900/60
        dark:bg-rose-950/30
        dark:text-rose-400
      "
    >
      <span
        className="
          h-1.5 w-1.5
          rounded-full
          bg-rose-500
          shadow-[0_0_5px_rgba(244,63,94,0.5)]
        "
      />

      <span className="text-slate-500 dark:text-slate-400">
        Session expires:
      </span>

      <span className="font-bold">
        {expiryTime}
      </span>
    </div>

  </div>


  {/* ================= PAGE HEADER ================= */}
  <div className="w-full">

    <div
      className="
        flex w-full
        flex-wrap items-center
        justify-between
        gap-3
        border-b-2
        border-indigo-100
        bg-gradient-to-r
        from-white
        via-white
        to-indigo-50/60
        px-3 py-2

        dark:border-indigo-900/50
        dark:from-slate-900
        dark:via-slate-900
        dark:to-indigo-950/30
      "
    >

      {/* Page Name */}
      <div
        className="
          flex min-w-0
          items-center
          border-l-4
          border-indigo-500
          pl-3
        "
      >
        {/* sm:text-base */}
        <h6
          className="
            truncate
            text-sm
            font-bold
            uppercase
            tracking-wide
            text-slate-700

            

            dark:text-slate-100
          "
        >
          {pageName}
        </h6>
      </div>


      {/* Back Button */}
      {prevPage && prevPage?.show && (
        <Chip
          size="sm"
          radius="md"
          variant="flat"
          className="
            cursor-pointer
            border border-indigo-200
            bg-indigo-50
            px-3
            font-semibold
            text-indigo-700

            transition-all
            duration-200

            hover:-translate-x-0.5
            hover:border-indigo-300
            hover:bg-indigo-600
            hover:text-white
            hover:shadow-md

            dark:border-indigo-800
            dark:bg-indigo-950/40
            dark:text-indigo-300
            dark:hover:bg-indigo-600
            dark:hover:text-white
          "
          startContent={
            <FontAwesomeIcon
              icon={faChevronLeft}
              className="text-[10px]"
            />
          }
          onClick={prevPage?.onClick}
        >
          Back
        </Chip>
      )}

    </div>


    {/* ================= PAGE INFORMATION ================= */}
    {info?.length > 0 && (
      <div
        className="
          flex flex-wrap
          items-center
          gap-x-3 gap-y-1
          border-b
          border-slate-100
          bg-slate-50/70
          px-4 py-1.5

          dark:border-slate-800
          dark:bg-slate-900/60
        "
      >

        {info.map((infoItem, index) => (
          <div
            key={index}
            className="
              flex items-center
              gap-1
              text-xs
              text-slate-600
              dark:text-slate-400
            "
          >

            <FontAwesomeIcon
              icon={faArrowAltCircleRight}
              className="
                text-[11px]
                text-indigo-500
                dark:text-indigo-400
              "
            />

            <span
              className="
                font-medium
                text-slate-600
                dark:text-slate-300
              "
            >
              {infoItem?.map((item) => `${item} `)}
            </span>

          </div>
        ))}

      </div>
    )}

  </div>

</div>
      </CardHeader>
      <CardBody>{children}</CardBody>
      <CardFooter></CardFooter>
    </Card>
  )
}
